import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, MessageSquare, Plus, X } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  addCrmCardActivity,
  chatTitle,
  getCrmCard,
  listFunnels,
  moveCrmStage,
  updateCrmCard,
  type CrmActivityItem,
  type UpdateCrmCardInput,
} from '@/entities/chats';
import {
  addCrmCardContact,
  linkCrmCardContact,
  listCrmContacts,
  unlinkCrmCardContact,
} from '@/entities/crm-contacts';
import { listAmoCRMUsers } from '@/entities/user';
import { ApiError } from '@/shared/api';
import {
  formatDateTime,
  crmName,
  labelOf,
  useI18n,
  type Locale,
  type Messages,
} from '@/shared/i18n';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { Input } from '@/shared/ui/shadcn/input';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { HeaderAction, PageHeader } from '@/widgets/page-header';

const LEAD_BUCKETS = ['hot', 'warm', 'junk'] as const;
const LEAD_SOURCES = ['quiz', 'alliance', 'marketplace'] as const;
const INTERLOCUTOR_ROLES = ['buyer', 'agent', 'owner', 'other'] as const;
const DIALOG_STATUSES = ['ai_active', 'operator_owned', 'escalated', 'closed'] as const;

function Field({ label, value }: { label: string; value?: string }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-[11px] tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd
        className={
          value
            ? 'text-sm break-words whitespace-pre-wrap'
            : 'text-sm text-muted-foreground'
        }
      >
        {value || '—'}
      </dd>
    </div>
  );
}

function sameStage(left: string, right: string) {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function activityFieldLabel(t: Messages, field?: string) {
  const key = field?.trim() ?? '';
  if (!key) return '';
  switch (key) {
    case 'name':
      return t.crm.nameLabel;
    case 'pipeline':
      return t.common.funnel;
    case 'stage':
      return t.common.stage;
    case 'phone':
      return t.common.phone;
    case 'summary':
    case 'description':
      return t.crm.summary;
    case 'price':
      return t.fields.budget;
    case 'responsible':
      return t.crm.responsible;
    case 'lead_bucket':
      return t.crm.leadBucket;
    case 'lead_source':
      return t.crm.leadSource;
    case 'role':
      return t.common.role;
    case 'status':
      return t.dialogStatus.label;
    default:
      return labelOf(t.fields, key);
  }
}

function formatActivityValue(t: Messages, field: string | undefined, value: string | undefined) {
  const text = value?.trim() ?? '';
  if (!text) return t.crm.activityCleared;
  if (field === 'pipeline' || field === 'stage') return crmName(t.crm.names, text);
  if (field === 'price') {
    const amount = Number(text);
    if (Number.isFinite(amount) && amount > 0) return `${amount.toLocaleString('ru-RU')} ฿`;
  }
  if (field === 'lead_bucket') return labelOf(t.fields, text) || text;
  if (field === 'lead_source') return labelOf(t.fields, text) || text;
  if (field === 'role') return labelOf(t.roles, text) || text;
  if (field === 'status') return labelOf(t.dialogStatus, text) || text;
  return text;
}

function activitySourceLabel(t: Messages, source?: string) {
  const value = source?.trim().toLowerCase() ?? '';
  if (value === 'agatha' || value === '') return value === 'agatha' ? 'Agatha' : '';
  if (value === 'operator') return t.crm.activityFromOperator;
  if (value === 'amocrm') return t.crm.activityFromAmo;
  return source ?? '';
}

function ActivityRow({
  item,
  t,
  locale,
}: {
  item: CrmActivityItem;
  t: Messages;
  locale: Locale;
}) {
  const when = item.created_at ? formatDateTime(item.created_at, locale) : '';
  if (item.kind === 'field_change' || item.kind === 'stage_change') {
    const label = activityFieldLabel(t, item.field);
    const source = activitySourceLabel(t, item.source);
    return (
      <li className="space-y-1">
        <p className="text-[11px] text-muted-foreground">
          {[source || item.author, when].filter(Boolean).join(' · ')}
        </p>
        <p className="text-sm break-words">
          <span className="text-muted-foreground">{label}: </span>
          {formatActivityValue(t, item.field, item.old_value)}
          {' → '}
          {formatActivityValue(t, item.field, item.new_value)}
        </p>
        {item.text?.trim() ? (
          <p className="text-sm break-words whitespace-pre-wrap">{item.text}</p>
        ) : null}
      </li>
    );
  }
  return (
    <li className="space-y-1">
      <p className="text-[11px] text-muted-foreground">
        {[item.author || activitySourceLabel(t, item.source), when].filter(Boolean).join(' · ')}
      </p>
      <p className="text-sm break-words whitespace-pre-wrap">{item.text}</p>
    </li>
  );
}

function parseBudgetInput(raw: string): number {
  const digits = raw.replaceAll(/[^\d]/g, '');
  if (!digits) return 0;
  const value = Number(digits);
  return Number.isFinite(value) ? value : 0;
}

export function CrmCardPage() {
  const { t, locale } = useI18n();
  const { cardId } = useParams();
  const [search] = useSearchParams();
  const queryClient = useQueryClient();
  const [pendingStage, setPendingStage] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [activityComment, setActivityComment] = useState('');
  const [nameDraft, setNameDraft] = useState('');
  const [summaryDraft, setSummaryDraft] = useState('');
  const [budgetDraft, setBudgetDraft] = useState('');
  const [bucketDraft, setBucketDraft] = useState('');
  const [sourceDraft, setSourceDraft] = useState('');
  const [createContactOpen, setCreateContactOpen] = useState(false);
  const [linkContactOpen, setLinkContactOpen] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [linkQuery, setLinkQuery] = useState('');

  const cardQuery = useQuery({
    queryKey: ['crm-card', cardId],
    queryFn: () => getCrmCard(cardId ?? ''),
    enabled: Boolean(cardId),
  });
  const funnelsQuery = useQuery({ queryKey: ['crm-funnels'], queryFn: listFunnels });
  const amoUsersQuery = useQuery({
    queryKey: ['amocrm-users'],
    queryFn: listAmoCRMUsers,
  });
  const allContactsQuery = useQuery({
    queryKey: ['crm-contacts', linkQuery],
    queryFn: () => listCrmContacts(linkQuery),
    enabled: linkContactOpen,
  });

  const card = cardQuery.data;
  const pipeline = card?.pipeline ?? search.get('pipeline') ?? '';
  const backTo = pipeline ? `/crm?pipeline=${encodeURIComponent(pipeline)}` : '/crm';
  const stages = useMemo(() => {
    const funnel = (funnelsQuery.data ?? []).find((item) => item.name === card?.pipeline);
    const list = [...(funnel?.stages ?? [])];
    if (card?.stage && !list.some((stage) => sameStage(stage, card.stage))) {
      list.push(card.stage);
    }
    return list;
  }, [funnelsQuery.data, card?.pipeline, card?.stage]);

  useEffect(() => {
    if (!card) return;
    setNameDraft(card.display_name ?? '');
    setSummaryDraft(card.summary ?? '');
    setBudgetDraft(card.price && card.price > 0 ? String(card.price) : '');
    setBucketDraft(card.fields?.lead_bucket?.trim() ?? '');
    setSourceDraft(card.fields?.lead_source?.trim() ?? '');
  }, [
    card?.id,
    card?.display_name,
    card?.summary,
    card?.price,
    card?.fields?.lead_bucket,
    card?.fields?.lead_source,
  ]);

  const move = useMutation({
    mutationFn: (input: { stage: string; note: string }) =>
      moveCrmStage(cardId ?? '', { stage: input.stage, note: input.note }),
    onSuccess: async () => {
      setPendingStage(null);
      setNote('');
      await queryClient.invalidateQueries({ queryKey: ['crm-card', cardId] });
      await queryClient.invalidateQueries({ queryKey: ['crm-cards'] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.moveFailed);
    },
  });

  const save = useMutation({
    mutationFn: (input: UpdateCrmCardInput) => updateCrmCard(cardId ?? '', input),
    onSuccess: async () => {
      toast.success(t.crm.saved);
      await queryClient.invalidateQueries({ queryKey: ['crm-card', cardId] });
      await queryClient.invalidateQueries({ queryKey: ['crm-cards'] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.saveFailed);
    },
  });

  const postActivity = useMutation({
    mutationFn: (text: string) => addCrmCardActivity(cardId ?? '', { text }),
    onSuccess: async () => {
      setActivityComment('');
      toast.success(t.crm.saved);
      await queryClient.invalidateQueries({ queryKey: ['crm-card', cardId] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.activityCommentFailed);
    },
  });

  const addContact = useMutation({
    mutationFn: (input: { name: string; phone?: string }) =>
      addCrmCardContact(cardId ?? '', input),
    onSuccess: async () => {
      setCreateContactOpen(false);
      setNewContactName('');
      setNewContactPhone('');
      toast.success(t.crm.saved);
      await queryClient.invalidateQueries({ queryKey: ['crm-card', cardId] });
      await queryClient.invalidateQueries({ queryKey: ['crm-contacts'] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.contactAddFailed);
    },
  });

  const linkContact = useMutation({
    mutationFn: (contactId: string) => linkCrmCardContact(cardId ?? '', contactId),
    onSuccess: async () => {
      setLinkContactOpen(false);
      setLinkQuery('');
      toast.success(t.crm.saved);
      await queryClient.invalidateQueries({ queryKey: ['crm-card', cardId] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.contactLinkFailed);
    },
  });

  const unlinkContact = useMutation({
    mutationFn: (contactId: string) => unlinkCrmCardContact(cardId ?? '', contactId),
    onSuccess: async () => {
      toast.success(t.crm.saved);
      await queryClient.invalidateQueries({ queryKey: ['crm-card', cardId] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.contactUnlinkFailed);
    },
  });

  const missing = cardQuery.error instanceof ApiError && cardQuery.error.status === 404;
  const contacts = card?.contacts ?? [];
  const linkedIds = useMemo(() => new Set(contacts.map((item) => item.id).filter(Boolean)), [contacts]);
  const linkCandidates = useMemo(
    () => (allContactsQuery.data ?? []).filter((item) => !linkedIds.has(item.id)),
    [allContactsQuery.data, linkedIds],
  );
  const amoUsers = amoUsersQuery.data ?? [];
  const saving =
    save.isPending ||
    postActivity.isPending ||
    addContact.isPending ||
    linkContact.isPending ||
    unlinkContact.isPending;
  const hasDialog = Boolean(card?.dialog_id);

  const commitName = () => {
    if (!card) return;
    const next = nameDraft.trim();
    if (!next || next === (card.display_name ?? '').trim()) return;
    save.mutate({ display_name: next });
  };

  const commitSummary = () => {
    if (!card) return;
    const next = summaryDraft.trim();
    if (next === (card.summary ?? '').trim()) return;
    save.mutate({ summary: next });
  };

  const commitBudget = () => {
    if (!card) return;
    const next = parseBudgetInput(budgetDraft);
    const current = card.price && card.price > 0 ? card.price : 0;
    if (next === current) return;
    save.mutate({ price: next });
  };

  const commitBucket = (value: string | null) => {
    if (!card) return;
    const next = (value ?? '').trim().toLowerCase();
    const current = card.fields?.lead_bucket?.trim().toLowerCase() ?? '';
    if (next === current) return;
    setBucketDraft(next);
    save.mutate({ lead_bucket: next });
  };

  const commitSource = (value: string | null) => {
    if (!card) return;
    const next = (value ?? '').trim().toLowerCase();
    const current = card.fields?.lead_source?.trim().toLowerCase() ?? '';
    if (next === current) return;
    setSourceDraft(next);
    save.mutate({ lead_source: next });
  };

  const commitResponsible = (value: string | null) => {
    if (!card) return;
    const next = value === '__none__' || !value ? 0 : Number(value);
    const current = card.responsible_amocrm_user_id ?? 0;
    if (!Number.isFinite(next) || next === current) return;
    save.mutate({ responsible_amocrm_user_id: next });
  };

  const commitRole = (value: string | null) => {
    if (!card || !hasDialog || !value) return;
    if (value === (card.role ?? '').trim()) return;
    save.mutate({ role: value });
  };

  const commitStatus = (value: string | null) => {
    if (!card || !hasDialog || !value) return;
    if (value === (card.status ?? '').trim()) return;
    save.mutate({ status: value });
  };

  return (
    <div className="absolute inset-0 flex min-h-0 flex-col overflow-hidden">
      <PageHeader
        title={card ? chatTitle(card) || t.common.lead : t.crm.cardTitle}
        description={
          card
            ? [crmName(t.crm.names, card.pipeline), crmName(t.crm.names, card.stage)]
                .filter(Boolean)
                .join(' · ')
            : t.crm.cardDescription
        }
        leadingMode="always"
        leading={
          <HeaderAction label={t.crm.back} render={<Link to={backTo} />}>
            <ArrowLeft className="size-4" />
          </HeaderAction>
        }
        actions={
          card?.dialog_id ? (
            <HeaderAction
              label={t.crm.openChat}
              render={<Link to={`/chats/${card.dialog_id}`} />}
            >
              <MessageSquare className="size-4" />
            </HeaderAction>
          ) : (
            <HeaderAction label={t.crm.openChat} disabled>
              <MessageSquare className="size-4" />
            </HeaderAction>
          )
        }
      />
      <ScrollArea className="min-h-0 flex-1">
        {cardQuery.isLoading ? (
          <p className="p-4 text-sm text-muted-foreground">{t.common.loading}</p>
        ) : missing ? (
          <p className="p-4 text-sm text-destructive">{t.crm.cardNotFound}</p>
        ) : cardQuery.isError ? (
          <p className="p-4 text-sm text-destructive">{t.crm.cardLoadFailed}</p>
        ) : card ? (
          <div className="mx-auto grid min-h-full w-full max-w-5xl desktop:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="flex min-w-0 flex-col gap-6 p-4 desktop:pr-8">
              <section className="space-y-2">
                <h2 className="text-[11px] tracking-wide text-muted-foreground uppercase">
                  {t.crm.summary}
                </h2>
                <Textarea
                  value={summaryDraft}
                  onChange={(event) => setSummaryDraft(event.target.value)}
                  onBlur={commitSummary}
                  placeholder={t.crm.summaryPlaceholder}
                  rows={8}
                  disabled={saving}
                  className="min-h-40"
                />
              </section>

              <section className="space-y-3 border-t pt-4">
                <h2 className="text-[11px] tracking-wide text-muted-foreground uppercase">
                  {t.crm.activity}
                </h2>
                <form
                  className="space-y-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const text = activityComment.trim();
                    if (!text || postActivity.isPending) return;
                    postActivity.mutate(text);
                  }}
                >
                  <Textarea
                    value={activityComment}
                    onChange={(event) => setActivityComment(event.target.value)}
                    placeholder={t.crm.activityCommentPlaceholder}
                    rows={3}
                    disabled={postActivity.isPending}
                    aria-label={t.crm.activityCommentPlaceholder}
                  />
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      size="sm"
                      disabled={postActivity.isPending || activityComment.trim() === ''}
                    >
                      {postActivity.isPending ? t.common.saving : t.crm.activityCommentSend}
                    </Button>
                  </div>
                </form>
                {(card.activity?.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">{t.crm.activityEmpty}</p>
                ) : (
                  <ol className="space-y-3">
                    {card.activity?.map((item) => (
                      <ActivityRow key={`${item.kind}-${item.id}`} item={item} t={t} locale={locale} />
                    ))}
                  </ol>
                )}
              </section>
            </div>

            <aside className="flex min-w-0 flex-col gap-6 p-4 desktop:border-l desktop:pl-6">
              <dl className="grid gap-4">
                <div className="space-y-0.5">
                  <dt className="text-[11px] tracking-wide text-muted-foreground uppercase">
                    {t.crm.nameLabel}
                  </dt>
                  <dd>
                    <Input
                      value={nameDraft}
                      onChange={(event) => setNameDraft(event.target.value)}
                      onBlur={commitName}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.currentTarget.blur();
                        }
                      }}
                      disabled={saving}
                      aria-label={t.crm.nameLabel}
                    />
                  </dd>
                </div>
                {stages.length > 0 ? (
                  <div className="space-y-0.5">
                    <dt className="text-[11px] tracking-wide text-muted-foreground uppercase">
                      {t.common.stage}
                    </dt>
                    <dd>
                      <Select
                        value={stages.find((stage) => sameStage(stage, card.stage)) ?? card.stage}
                        onValueChange={(value) => {
                          if (!value || sameStage(value, card.stage)) return;
                          setNote('');
                          setPendingStage(value);
                        }}
                      >
                        <SelectTrigger className="w-full" aria-label={t.crm.moveStage}>
                          <SelectValue>{(value) => crmName(t.crm.names, value)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {stages.map((stage) => (
                            <SelectItem key={stage} value={stage}>
                              {crmName(t.crm.names, stage)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </dd>
                  </div>
                ) : (
                  <Field label={t.common.stage} value={crmName(t.crm.names, card.stage)} />
                )}
                <div className="space-y-0.5">
                  <dt className="text-[11px] tracking-wide text-muted-foreground uppercase">
                    {t.fields.budget}
                  </dt>
                  <dd>
                    <Input
                      value={budgetDraft}
                      onChange={(event) => setBudgetDraft(event.target.value)}
                      onBlur={commitBudget}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.currentTarget.blur();
                        }
                      }}
                      placeholder={t.crm.budgetPlaceholder}
                      inputMode="numeric"
                      disabled={saving}
                      aria-label={t.fields.budget}
                    />
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-[11px] tracking-wide text-muted-foreground uppercase">
                    {t.crm.leadBucket}
                  </dt>
                  <dd>
                    <Select
                      value={bucketDraft || '__none__'}
                      onValueChange={(value) => {
                        if (value == null) return;
                        commitBucket(value === '__none__' ? '' : value);
                      }}
                      disabled={saving}
                    >
                      <SelectTrigger className="w-full" aria-label={t.crm.leadBucket}>
                        <SelectValue>
                          {(value) =>
                            !value || value === '__none__'
                              ? t.crm.leadBucketNone
                              : labelOf(t.fields, String(value)) || String(value)
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t.crm.leadBucketNone}</SelectItem>
                        {LEAD_BUCKETS.map((bucket) => (
                          <SelectItem key={bucket} value={bucket}>
                            {labelOf(t.fields, bucket) || bucket}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-[11px] tracking-wide text-muted-foreground uppercase">
                    {t.crm.leadSource}
                  </dt>
                  <dd>
                    <Select
                      value={sourceDraft || '__none__'}
                      onValueChange={(value) => {
                        if (value == null) return;
                        commitSource(value === '__none__' ? '' : value);
                      }}
                      disabled={saving}
                    >
                      <SelectTrigger className="w-full" aria-label={t.crm.leadSource}>
                        <SelectValue>
                          {(value) =>
                            !value || value === '__none__'
                              ? t.crm.leadSourceNone
                              : labelOf(t.fields, String(value)) || String(value)
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t.crm.leadSourceNone}</SelectItem>
                        {LEAD_SOURCES.map((source) => (
                          <SelectItem key={source} value={source}>
                            {labelOf(t.fields, source) || source}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-[11px] tracking-wide text-muted-foreground uppercase">
                    {t.crm.responsible}
                  </dt>
                  <dd>
                    <Select
                      value={
                        card.responsible_amocrm_user_id
                          ? String(card.responsible_amocrm_user_id)
                          : '__none__'
                      }
                      onValueChange={(value) => {
                        if (value == null) return;
                        commitResponsible(value);
                      }}
                      disabled={saving}
                    >
                      <SelectTrigger className="w-full" aria-label={t.crm.responsible}>
                        <SelectValue>
                          {(value) => {
                            if (!value || value === '__none__') return t.crm.responsibleNone;
                            const user = amoUsers.find(
                              (item) => String(item.amocrm_user_id) === String(value),
                            );
                            return (
                              user?.agata_user_name ||
                              user?.name ||
                              card.responsible_name ||
                              String(value)
                            );
                          }}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t.crm.responsibleNone}</SelectItem>
                        {amoUsers.map((user) => (
                          <SelectItem
                            key={user.amocrm_user_id}
                            value={String(user.amocrm_user_id)}
                          >
                            {user.agata_user_name || user.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-[11px] tracking-wide text-muted-foreground uppercase">
                    {t.common.role}
                  </dt>
                  <dd>
                    {hasDialog ? (
                      <Select
                        value={card.role || undefined}
                        onValueChange={(value) => {
                          if (value == null) return;
                          commitRole(value);
                        }}
                        disabled={saving}
                      >
                        <SelectTrigger className="w-full" aria-label={t.common.role}>
                          <SelectValue>
                            {(value) => labelOf(t.roles, String(value ?? '')) || t.common.empty}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {INTERLOCUTOR_ROLES.map((role) => (
                            <SelectItem key={role} value={role}>
                              {labelOf(t.roles, role) || role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t.crm.dialogNone}</p>
                    )}
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-[11px] tracking-wide text-muted-foreground uppercase">
                    {t.dialogStatus.label}
                  </dt>
                  <dd>
                    {hasDialog ? (
                      <Select
                        value={card.status || undefined}
                        onValueChange={(value) => {
                          if (value == null) return;
                          commitStatus(value);
                        }}
                        disabled={saving}
                      >
                        <SelectTrigger className="w-full" aria-label={t.dialogStatus.label}>
                          <SelectValue>
                            {(value) =>
                              labelOf(t.dialogStatus, String(value ?? '')) || t.common.empty
                            }
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {DIALOG_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {labelOf(t.dialogStatus, status) || status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t.crm.dialogNone}</p>
                    )}
                  </dd>
                </div>
              </dl>

              <section className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-[11px] tracking-wide text-muted-foreground uppercase">
                    {t.crm.contacts}
                  </h2>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      render={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          disabled={saving}
                          aria-label={t.crm.contactAdd}
                        />
                      }
                    >
                      <Plus className="size-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          setNewContactName('');
                          setNewContactPhone('');
                          setCreateContactOpen(true);
                        }}
                      >
                        {t.crm.contactCreate}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setLinkQuery('');
                          setLinkContactOpen(true);
                        }}
                      >
                        {t.crm.contactLink}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {contacts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t.crm.contactsEmpty}</p>
                ) : (
                  <ul className="space-y-1">
                    {contacts.map((contact) => (
                      <li
                        key={contact.id || contact.name}
                        className="group flex items-center gap-1 rounded-md px-1 py-1 hover:bg-muted/60"
                      >
                        {contact.id ? (
                          <Link
                            to={`/contacts/${encodeURIComponent(contact.id)}?card=${encodeURIComponent(card.id)}`}
                            className="min-w-0 flex-1 truncate text-sm font-medium hover:underline"
                          >
                            {contact.name}
                            {contact.is_main ? (
                              <span className="ml-2 text-[11px] font-normal tracking-wide text-muted-foreground uppercase">
                                {t.crm.contactMain}
                              </span>
                            ) : null}
                          </Link>
                        ) : (
                          <span className="min-w-0 flex-1 truncate text-sm font-medium">
                            {contact.name}
                          </span>
                        )}
                        {contact.id ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            className="opacity-60 group-hover:opacity-100"
                            disabled={saving}
                            aria-label={t.crm.contactUnlink}
                            onClick={() => unlinkContact.mutate(contact.id)}
                          >
                            <X className="size-3.5" />
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </aside>
          </div>
        ) : null}
      </ScrollArea>

      <Dialog
        open={pendingStage !== null}
        onOpenChange={(open) => {
          if (!open && !move.isPending) {
            setPendingStage(null);
            setNote('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.crm.whatChanged}</DialogTitle>
            <DialogDescription>
              {card && pendingStage
                ? `${crmName(t.crm.names, card.stage)} → ${crmName(t.crm.names, pendingStage)} `
                : null}
              {t.crm.whatChangedHint}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t.crm.notePlaceholder}
            rows={4}
            autoFocus
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={move.isPending}
              onClick={() => {
                setPendingStage(null);
                setNote('');
              }}
            >
              {t.crm.cancel}
            </Button>
            <Button
              type="button"
              disabled={move.isPending || note.trim() === '' || !pendingStage}
              onClick={() => {
                if (!pendingStage) return;
                move.mutate({ stage: pendingStage, note: note.trim() });
              }}
            >
              {move.isPending ? t.common.saving : t.crm.saveNote}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={createContactOpen}
        onOpenChange={(open) => {
          if (!open && !addContact.isPending) {
            setCreateContactOpen(false);
            setNewContactName('');
            setNewContactPhone('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.crm.contactCreate}</DialogTitle>
            <DialogDescription>{t.crm.contactCreateHint}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={newContactName}
              onChange={(event) => setNewContactName(event.target.value)}
              placeholder={t.crm.contactNamePlaceholder}
              disabled={addContact.isPending}
              autoFocus
              aria-label={t.crm.contactNamePlaceholder}
            />
            <Input
              value={newContactPhone}
              onChange={(event) => setNewContactPhone(event.target.value)}
              placeholder={t.crm.contactPhonePlaceholder}
              disabled={addContact.isPending}
              aria-label={t.crm.contactPhonePlaceholder}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={addContact.isPending}
              onClick={() => {
                setCreateContactOpen(false);
                setNewContactName('');
                setNewContactPhone('');
              }}
            >
              {t.crm.cancel}
            </Button>
            <Button
              type="button"
              disabled={addContact.isPending || newContactName.trim() === ''}
              onClick={() =>
                addContact.mutate({
                  name: newContactName.trim(),
                  phone: newContactPhone.trim() || undefined,
                })
              }
            >
              {addContact.isPending ? t.common.creating : t.crm.contactCreate}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={linkContactOpen}
        onOpenChange={(open) => {
          if (!open && !linkContact.isPending) {
            setLinkContactOpen(false);
            setLinkQuery('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.crm.contactLinkTitle}</DialogTitle>
            <DialogDescription>{t.crm.contactLinkHint}</DialogDescription>
          </DialogHeader>
          <Input
            value={linkQuery}
            onChange={(event) => setLinkQuery(event.target.value)}
            placeholder={t.crm.contactsSearch}
            disabled={linkContact.isPending}
            autoFocus
            aria-label={t.crm.contactsSearch}
          />
          <ScrollArea className="h-64 rounded-md border">
            {allContactsQuery.isLoading ? (
              <p className="p-3 text-sm text-muted-foreground">{t.common.loading}</p>
            ) : linkCandidates.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">{t.crm.contactLinkEmpty}</p>
            ) : (
              <ul className="p-1">
                {linkCandidates.map((contact) => (
                  <li key={contact.id}>
                    <button
                      type="button"
                      className="flex w-full flex-col items-start gap-0.5 rounded-md px-3 py-2 text-left hover:bg-muted disabled:opacity-50"
                      disabled={linkContact.isPending}
                      onClick={() => linkContact.mutate(contact.id)}
                    >
                      <span className="text-sm font-medium">{contact.name}</span>
                      {contact.phone ? (
                        <span className="text-xs text-muted-foreground">{contact.phone}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={linkContact.isPending}
              onClick={() => {
                setLinkContactOpen(false);
                setLinkQuery('');
              }}
            >
              {t.crm.cancel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
